import { getApiBaseUrl } from './api';
import {
  publicBookingApiParamsObjectFromClubRouteKey,
  publicBookingApiParamsObjectFromWindow,
} from '../utils/club-route-key';
import type { CheckoutIntentResponse } from './payments';

export type ShopGiftCardLine = {
  type: 'gift_card';
  amountCents: number;
  recipientEmail?: string;
  recipientName?: string;
  message?: string;
};

export type ShopPassLine = {
  type: 'pass';
  passProductId: string;
};

export type ShopCartLine = ShopGiftCardLine | ShopPassLine;

export type ShopCatalogPass = {
  id: string;
  name: string;
  description?: string | null;
  category: string;
  passPriceCents: number;
  originalValueCents: number;
  totalQuantity: number;
  savingsAmountCents: number;
  savingsPercentage: number;
  taxClassId?: string | null;
  taxRate: number;
};

export type ShopCatalogMembership = {
  id: string;
  name: string;
  description?: string | null;
  priceCents: number | null;
  imageUrl?: string | null;
};

export type ShopGiftCardConfig = {
  enabled: boolean;
  presetAmountsCents: number[];
  minAmountCents: number;
  maxAmountCents: number;
};

export type ShopPublicPaymentMethod = {
  id: string;
  code: string;
  name: string;
  description?: string;
  requiresWallet: boolean;
  displayOrder: number;
  channel: 'card' | 'wallet' | 'gift_card';
  requiresStripeConnect: boolean;
};

export type ShopCatalog = {
  shopEnabled: boolean;
  paymentsReady: boolean;
  devPaymentBypassAvailable: boolean;
  paymentMethods: ShopPublicPaymentMethod[];
  giftCardConfig: ShopGiftCardConfig;
  passes: ShopCatalogPass[];
  memberships: ShopCatalogMembership[];
  membershipRequestsEnabled: boolean;
  clubName: string;
};

export type ShopGiftCardValidation = {
  valid: boolean;
  giftCardId?: string;
  codeMasked?: string;
  balance?: number;
  redeemAmountCents: number;
  remainderCents: number;
  requiresSecondaryPayment: boolean;
};

export type ShopQuoteLine = {
  type: ShopCartLine['type'];
  label: string;
  amountCents: number;
  taxCents: number;
  passProductId?: string;
};

export type ShopQuote = {
  currency: string;
  lines: ShopQuoteLine[];
  subtotalCents: number;
  taxCents: number;
  totalCents: number;
};

export type ShopCompleteResult = {
  quote: ShopQuote;
  paymentIntentId: string;
  paymentMethodCode: string;
  giftCards: Array<{ giftCardId: string; code?: string; amountCents: number }>;
  passes: Array<{ passSubscriptionId: string; passProductId: string }>;
};

function shopApiError(body: Record<string, unknown>, fallback: string): string {
  const message = typeof body.message === 'string' ? body.message : undefined;
  const error = typeof body.error === 'string' ? body.error : undefined;
  return message || error || fallback;
}

function authHeaders(): Record<string, string> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  try {
    const raw = localStorage.getItem('booking_service_auth');
    if (raw) {
      const session = JSON.parse(raw) as { token?: string };
      if (session.token) headers.Authorization = `Bearer ${session.token}`;
    }
  } catch {
    /* ignore */
  }
  return headers;
}

function clubParams(clubRouteKey?: string | null): Record<string, string> {
  if (clubRouteKey) return publicBookingApiParamsObjectFromClubRouteKey(clubRouteKey);
  return publicBookingApiParamsObjectFromWindow();
}

export async function fetchShopCatalog(clubRouteKey?: string | null): Promise<ShopCatalog> {
  const params = clubParams(clubRouteKey);
  const qs = new URLSearchParams(params).toString();
  const response = await fetch(`${getApiBaseUrl()}/public/shop/catalog?${qs}`);
  const body = await response.json();
  if (!response.ok) {
    throw new Error(shopApiError(body, 'Failed to load shop catalog'));
  }
  return body.data as ShopCatalog;
}

export async function quoteShopCart(
  lines: ShopCartLine[],
  clubRouteKey?: string | null,
): Promise<ShopQuote> {
  const response = await fetch(`${getApiBaseUrl()}/public/shop/quote`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ ...clubParams(clubRouteKey), lines }),
  });
  const body = await response.json();
  if (!response.ok) {
    throw new Error(shopApiError(body, 'Failed to quote cart'));
  }
  return body.data as ShopQuote;
}

export async function createShopCheckoutIntent(params: {
  lines: ShopCartLine[];
  customerId: string;
  clubRouteKey?: string | null;
  paymentMethodCode?: string;
  amountCents?: number;
}): Promise<CheckoutIntentResponse> {
  const response = await fetch(`${getApiBaseUrl()}/public/shop/checkout-intent`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({
      ...clubParams(params.clubRouteKey),
      customerId: params.customerId,
      lines: params.lines,
      paymentMethodCode: params.paymentMethodCode,
      amountCents: params.amountCents,
    }),
  });
  const body = await response.json();
  if (!response.ok) {
    throw new Error(shopApiError(body, 'Failed to start checkout'));
  }
  return body.data as CheckoutIntentResponse;
}

export async function validateShopGiftCard(params: {
  lines: ShopCartLine[];
  customerId: string;
  giftCardCode?: string;
  giftCardId?: string;
  clubRouteKey?: string | null;
}): Promise<ShopGiftCardValidation> {
  const response = await fetch(`${getApiBaseUrl()}/public/shop/validate-gift-card`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({
      ...clubParams(params.clubRouteKey),
      customerId: params.customerId,
      lines: params.lines,
      giftCardCode: params.giftCardCode,
      giftCardId: params.giftCardId,
    }),
  });
  const body = await response.json();
  if (!response.ok) {
    throw new Error(shopApiError(body, 'Failed to validate gift card'));
  }
  return body.data as ShopGiftCardValidation;
}

export async function completeShopOrder(params: {
  lines: ShopCartLine[];
  customerId: string;
  paymentMethodCode: string;
  paymentIntentId?: string;
  giftCardCode?: string;
  giftCardId?: string;
  secondaryPaymentMethodCode?: string;
  secondaryPaymentIntentId?: string;
  clubRouteKey?: string | null;
}): Promise<ShopCompleteResult> {
  const response = await fetch(`${getApiBaseUrl()}/public/shop/complete`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({
      ...clubParams(params.clubRouteKey),
      customerId: params.customerId,
      paymentMethodCode: params.paymentMethodCode,
      paymentIntentId: params.paymentIntentId,
      giftCardCode: params.giftCardCode,
      giftCardId: params.giftCardId,
      secondaryPaymentMethodCode: params.secondaryPaymentMethodCode,
      secondaryPaymentIntentId: params.secondaryPaymentIntentId,
      lines: params.lines,
    }),
  });
  const body = await response.json();
  if (!response.ok) {
    throw new Error(shopApiError(body, 'Failed to complete order'));
  }
  return body.data as ShopCompleteResult;
}

export type SubmitMembershipRequestResult = {
  requestId: string;
  status: string;
  product: { id: string; name: string };
  customerId: string;
  submittedAt: string;
  crm?: {
    stageId: string;
    stageName: string;
    pipelineId: string;
    pipelineName: string;
  };
};

export async function submitMembershipRequest(params: {
  productId: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  message?: string;
  clubRouteKey?: string | null;
}): Promise<SubmitMembershipRequestResult> {
  const response = await fetch(`${getApiBaseUrl()}/public/shop/membership-request`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      ...clubParams(params.clubRouteKey),
      productId: params.productId,
      firstName: params.firstName,
      lastName: params.lastName,
      email: params.email,
      phone: params.phone,
      message: params.message,
    }),
  });
  const body = await response.json();
  if (!response.ok) {
    throw new Error(shopApiError(body, 'Failed to submit membership request'));
  }
  return body.data as SubmitMembershipRequestResult;
}
